var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { useResponsiveProps, } from '@core-front/helper';
import { useTheme } from '@core-front/theme-provider';
const ButtonCore = (props) => {
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    return responsive ? React.createElement(ButtonResponsiveCore, Object.assign({}, rest, { responsive: responsive })) : React.createElement(ButtonInnerCore, Object.assign({}, rest));
};
const ButtonResponsiveCore = (props) => {
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    const { breakPoints } = useTheme();
    const responsiveProps = useResponsiveProps({ breakPoints, responsive });
    return React.createElement(ButtonInnerCore, Object.assign({}, rest, responsiveProps));
};
class ButtonInnerCore extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.handleClick = (event) => {
            if (!this.props.external && this.props.onClick) {
                this.props.onClick(event);
            }
        };
    }
    BEM() {
        const classArray = ['btn'];
        if (this.props.type) {
            classArray.push('btn--' + this.props.type);
        }
        if (this.props.size) {
            classArray.push('btn--' + this.props.size);
        }
        if (this.props.icon) {
            classArray.push('btn--icon-only');
        }
        if (this.props.variant) {
            classArray.push('btn--variant-' + this.props.variant);
        }
        if (this.props.link) {
            classArray.push('btn--link');
            if (this.props.isUnderlined) {
                classArray.push('btn--underlined');
            }
        }
        if (this.props.block) {
            classArray.push('btn--block');
        }
        if (this.props.isActive) {
            classArray.push('is-active');
        }
        if (this.props.isLoading) {
            classArray.push('is-loading');
        }
        if (this.props.verticalAlign) {
            classArray.push('btn--vertical-align-' + this.props.verticalAlign);
        }
        if (this.props.iconRight === '#arrow-right' && !this.props.link) {
            classArray.push('btn--with-arrow-right-icon');
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        return classArray.join(' ');
    }
    iconBEM(location) {
        const classArray = ['btn__icon'];
        classArray.push('btn__icon--' + location);
        if (((location === 'left' && this.props.iconLeftSize) || (location === 'right' && this.props.iconRightSize) || (location === 'center' && this.props.iconSize)) && this.props.link) {
            classArray.push('btn__icon--custom-size');
        }
        if (this.props.iconClass) {
            classArray.push(this.props.iconClass);
        }
        return classArray.join(' ');
    }
    iconWrapperBEM(location, size) {
        const classArray = ['btn__icon-wrapper', `btn__icon-wrapper--${location}`];
        if (size) {
            classArray.push(`btn__icon-wrapper--${size}`);
        }
        return classArray.join(' ');
    }
    getIcon(location, modifiers, size) {
        const { Icon } = this.props.components;
        return (React.createElement("span", { className: this.iconWrapperBEM(location, size) },
            React.createElement(Icon, { modifiers: modifiers, size: size, className: this.iconBEM(location) })));
    }
    renderContent() {
        const { Spinner } = this.props.components;
        return (React.createElement(React.Fragment, null,
            this.props.isLoading &&
                (React.createElement(CSSTransition, { in: this.props.isLoading, timeout: 300, classNames: "fade", unmountOnExit: true },
                    React.createElement(Spinner, { className: "btn__spinner" }))),
            React.createElement("span", { className: "btn__inner" },
                this.props.iconLeft && this.getIcon('left', this.props.iconLeft, this.props.iconLeftSize),
                this.props.icon && this.getIcon('center', this.props.icon, this.props.iconSize),
                React.createElement("span", { className: "btn__text" }, this.props.text),
                this.props.iconRight && this.getIcon('right', this.props.iconRight, this.props.iconRightSize))));
    }
    isAnchor(props) {
        return 'url' in props;
    }
    isSpan(props) {
        return props.element === 'span';
    }
    renderAnchor(props) {
        return (React.createElement("a", Object.assign({}, props.attributes, { 
            // common attributes of a, span, button
            className: this.BEM(), id: props.id, ref: props.buttonRef, "aria-label": this.props.ariaLabel, "aria-expanded": this.props['aria-expanded'], "aria-controls": this.props['aria-controls'], onKeyDown: this.props.onKeyDown, 
            // common attributes of a, button
            onClick: this.handleClick, 
            // a specific attributes
            href: props.url, target: props.target }), this.renderContent()));
    }
    renderSpan(props) {
        return (React.createElement("span", Object.assign({}, props.attributes, { 
            // common attributes of a, span, button
            className: this.BEM(), id: props.id, ref: props.buttonRef, "aria-label": this.props.ariaLabel, "aria-expanded": this.props['aria-expanded'], "aria-controls": this.props['aria-controls'], onKeyDown: this.props.onKeyDown }), this.renderContent()));
    }
    renderButton(props) {
        return (React.createElement("button", Object.assign({}, props.attributes, { 
            // common attributes of a, span, button
            className: this.BEM(), id: props.id, ref: props.buttonRef, "aria-label": this.props.ariaLabel, "aria-expanded": this.props['aria-expanded'], "aria-controls": this.props['aria-controls'], onKeyDown: this.props.onKeyDown, 
            // common attributes of a, button
            onClick: this.handleClick, 
            // button specific attributes
            type: (props.attributes && props.attributes.type) || 'button', disabled: props.disabled }), this.renderContent()));
    }
    renderSkeleton(props) {
        const { components: { Skeleton, SkeletonItem } } = this.props;
        const { skeleton } = props;
        return (React.createElement(Skeleton, { label: skeleton.label },
            React.createElement(SkeletonItem, { className: this.BEM(), width: skeleton.width })));
    }
    render() {
        if ('skeleton' in this.props) {
            return this.renderSkeleton(this.props);
        }
        if (this.isSpan(this.props)) {
            return this.renderSpan(this.props);
        }
        if (this.isAnchor(this.props)) {
            if (this.props.disabled) {
                // type cast as a hack since we need to allow disabling anchor buttons
                // but that is actually not supported in HTML so ¯\_(ツ)_/¯
                return this.renderButton(this.props);
            }
            return this.renderAnchor(this.props);
        }
        return this.renderButton(this.props);
    }
}
export { ButtonCore as default };
